import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { api } from '../utils/apiClient';
import type {
  CreateSurveyRequest,
  CreateSurveyResponse,
  GetSurveysResponse,
  SurveyFormData,
} from '../types/survey';

/**
 * Create a new survey
 * Requires authentication (token automatically included)
 * 
 * @param data - Survey data with questions
 * @returns Promise with survey creation response
 */
export const createSurvey = async (
  data: CreateSurveyRequest
): Promise<CreateSurveyResponse> => {
  return api.post<CreateSurveyResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.SURVEY.CREATE}`,
    data
  );
};

/**
 * Helper function to create survey from form data
 * Automatically formats the questions with proper order
 */
export const createSurveyFromForm = async (
  formData: SurveyFormData
): Promise<CreateSurveyResponse> => {
  // Ensure questions have proper order (1-indexed)
  const orderedQuestions = formData.questions.map((q, index) => ({
    ...q,
    question_order: index + 1, // Start from 1
  }));

  const requestData: CreateSurveyRequest = {
    campaign_id: formData.campaignId,
    survey_name: formData.surveyName,
    description: formData.description,
    creation_method: formData.creationMethod,
    questions: orderedQuestions,
  };

  return createSurvey(requestData);
};

/**
 * Get all surveys
 * Requires authentication (token automatically included)
 */
export const getSurveys = async (): Promise<GetSurveysResponse> => {
  return api.get<GetSurveysResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.SURVEY.LIST}`
  );
};

/**
 * Get surveys for a specific campaign
 * Requires authentication (token automatically included)
 */
export const getSurveysByCampaign = async (
  campaignId: string
): Promise<GetSurveysResponse> => {
  return api.get<GetSurveysResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.SURVEY.LIST}?campaign_id=${campaignId}`
  );
};

/**
 * Get a single survey by ID
 * Requires authentication (token automatically included)
 */
export const getSurveyById = async (
  surveyId: string
): Promise<CreateSurveyResponse> => {
  return api.get<CreateSurveyResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.SURVEY.GET}/${surveyId}`
  );
};

/**
 * Update a survey
 * Requires authentication (token automatically included)
 */
export const updateSurvey = async (
  surveyId: string,
  data: Partial<CreateSurveyRequest>
): Promise<CreateSurveyResponse> => {
  return api.put<CreateSurveyResponse>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.SURVEY.UPDATE}/${surveyId}`,
    data
  );
};

/**
 * Delete a survey
 * Requires authentication (token automatically included)
 */
export const deleteSurvey = async (
  surveyId: string
): Promise<{ success: boolean; message?: string }> => {
  return api.delete<{ success: boolean; message?: string }>(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.SURVEY.DELETE}/${surveyId}`
  );
};

